import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import { PACKAGE_TYPE_LABELS } from "../../utils/packageLabels";
import LoadingIndicator from "../../components/LoadingIndicator";
import {
  getActiveOrders,
  listApprovedRiders,
  reassignOrder,
  adminSetOrderPaymentMethod,
  adminCompleteOrder,
  setTruckPrice,
  getPricingSettings
} from "../../api/client";

const TRUCK_PRICING_LABEL = {
  pending: { cls: "warning", label: "⏳ Awaiting Pricing" },
  admin_priced: { cls: "info", label: "✅ Admin Priced" },
  auto_priced: { cls: "warning", label: "🤖 Auto-Quoted (placeholder)" }
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [launchedCities, setLaunchedCities] = useState(["Lagos", "Abuja", "Port Harcourt"]);
  const [loading, setLoading] = useState(true);
  const [reassigning, setReassigning] = useState(null); // order._id being reassigned
  const [riderPickerOrderId, setRiderPickerOrderId] = useState(null);
  const [truckPriceMap, setTruckPriceMap] = useState({});
  const [truckPricingBusy, setTruckPricingBusy] = useState(null);
  const [paymentMethodBusy, setPaymentMethodBusy] = useState(null);
  const [completeBusy, setCompleteBusy] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, rRes] = await Promise.all([
        getActiveOrders(selectedCity || undefined, selectedStatus),
        listApprovedRiders(selectedCity || undefined)
      ]);
      setOrders(oRes.data);
      setRiders(rRes.data);
    } catch {
      setError("Could not load orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedCity, selectedStatus]);
  useEffect(() => {
    getPricingSettings().then(r => {
      if (r.data.launchedCities?.length) setLaunchedCities(r.data.launchedCities);
    }).catch(() => {});
  }, []);

  const handleReassign = async (orderId, riderId) => {
    setError("");
    setReassigning(orderId);
    try {
      await reassignOrder(orderId, riderId);
      setRiderPickerOrderId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Reassignment failed.");
    } finally {
      setReassigning(null);
    }
  };

  const handleSwitchPaymentMethod = async (orderId, paymentMethod) => {
    setError("");
    setPaymentMethodBusy(orderId);
    try {
      await adminSetOrderPaymentMethod(orderId, paymentMethod);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not change payment method.");
    } finally {
      setPaymentMethodBusy(null);
    }
  };

  const handleCompleteOrder = async (orderId) => {
    if (!window.confirm("Force-complete this order? This settles the dispatcher's wallet immediately, skipping any remaining delivery steps.")) return;
    setError("");
    setCompleteBusy(orderId);
    try {
      await adminCompleteOrder(orderId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not complete this order.");
    } finally {
      setCompleteBusy(null);
    }
  };

  const handleSetTruckPrice = async (orderId) => {
    const total = Number(truckPriceMap[orderId]);
    if (!total || total <= 0) return setError("Enter a valid truck price.");
    setError("");
    setTruckPricingBusy(orderId);
    try {
      await setTruckPrice(orderId, total);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not set truck price.");
    } finally {
      setTruckPricingBusy(null);
    }
  };

  const STATUS_COLORS = {
    scheduled: "info",
    requested: "warning",
    accepted: "",
    arrived_at_pickup: "",
    picked_up: "",
    in_transit: "",
    delivered: "warning",
    completed: "",
    cancelled: "danger",
    failed: "danger"
  };

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

      <div className="brand-header">
        <span className="brand-logo">📦</span>
        <span className="brand-name">Orders</span>
        <p className="brand-tagline">Monitor, reassign, and review deliveries</p>
      </div>

      {/* FILTERS */}
      <div className="row" style={{ marginBottom: "16px", alignItems: "flex-end" }}>
        <div className="form-field" style={{ maxWidth: "240px", marginBottom: 0 }}>
          <label>Filter by City</label>
          <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)}>
            <option value="">All Launched Cities</option>
            {launchedCities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-field" style={{ maxWidth: "240px", marginBottom: 0 }}>
          <label>Filter by Status</label>
          <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
            <option value="active">Active (in progress)</option>
            <option value="all">All Orders</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <LoadingIndicator label="Loading orders..." />
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <span className="empty-state-icon">✅</span>
          <p className="empty-state-text">No active orders right now.</p>
        </div>
      ) : (
        orders.map(order => (
          <div key={order._id} className="rider-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <div>
                <strong>Order #{order._id.slice(-6).toUpperCase()}</strong>
                <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                  {new Date(order.createdAt).toLocaleString()}
                </p>
              </div>
              <span className={`status-badge ${STATUS_COLORS[order.status] || ""}`}>
                {order.status.replace(/_/g, " ")}
              </span>
            </div>

            {order.status === "requested" && (
              <div className="info-row">
                <span className="label">Dispatch</span>
                <span className="value">
                  {order.offer?.status === "pending"
                    ? "🕐 Offered to a dispatcher — awaiting accept/pass"
                    : "📋 Open on job board — unclaimed"}
                </span>
              </div>
            )}

            {order.issue?.status === "open" && (
              <div className="info-row" style={{ background: "var(--warning-bg)", padding: "6px 10px", borderRadius: "6px" }}>
                <span className="label" style={{ color: "var(--warning-hover)", fontWeight: 700 }}>🚨 Open Issue</span>
                <span className="value">{order.issue.reason?.replace(/_/g, " ")}</span>
              </div>
            )}

            {order.status === "cancelled" && order.cancellation?.by && (
              <div className="info-row" style={{ background: "rgba(239,68,68,0.08)", padding: "6px 10px", borderRadius: "6px", marginBottom: "6px" }}>
                <span className="label" style={{ color: "var(--danger-hover)" }}>Cancelled by</span>
                <span className="value">{order.cancellation.by} — {order.cancellation.reason?.replace(/_/g, " ")}{order.cancellation.note ? `: ${order.cancellation.note}` : ""}</span>
              </div>
            )}

            <div className="info-row">
              <span className="label">Customer</span>
              <span className="value">{order.user?.name} ({order.user?.phone})</span>
            </div>
            <div className="info-row">
              <span className="label">Vehicle</span>
              <span className="value">{order.vehicleType?.toUpperCase() || "BIKE"}{order.receiverAcknowledgement?.enabled ? ` · RA: ${order.receiverAcknowledgement.method}` : ""}</span>
            </div>
            <div className="info-row">
              <span className="label">Package Size</span>
              <span className="value">{PACKAGE_TYPE_LABELS[order.packageType] || order.packageType}</span>
            </div>
            {order.vehicleType === "truck" ? (
              (order.packageDescription || order.cargoWeight) && (
                <div className="info-row">
                  <span className="label">Cargo</span>
                  <span className="value">{order.packageDescription || "-"}{order.cargoWeight ? ` · ${order.cargoWeight}` : ""}</span>
                </div>
              )
            ) : (
              order.packageDescription && (
                <div className="info-row">
                  <span className="label">Package Description</span>
                  <span className="value">{order.packageDescription}</span>
                </div>
              )
            )}
            <div className="info-row">
              <span className="label">Pickup</span>
              <span className="value">{order.pickup.address} {order.pickupContact?.name ? `(Contact: ${order.pickupContact.name} - ${order.pickupContact.phone})` : ""}</span>
            </div>
            <div className="info-row">
              <span className="label">Drop-off</span>
              <span className="value">{order.dropoff.address} {order.dropoffContact?.name ? `(Contact: ${order.dropoffContact.name} - ${order.dropoffContact.phone})` : ""}</span>
            </div>
            {order.stops?.length > 0 && (
              <div className="info-row">
                <span className="label">Stops</span>
                <span className="value">{order.stops.length} intermediate stop{order.stops.length > 1 ? "s" : ""}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Dispatcher</span>
              <span className="value">{order.rider ? order.rider.name : <span style={{ color: "var(--warning)" }}>Unassigned</span>}</span>
            </div>
            <div className="info-row">
              <span className="label">Total</span>
              <span className="value">
                ₦{order.priceSnapshot.total}
                {order.vehicleType === "truck" && order.truckPricing?.status === "pending" && (
                  <span style={{ color: "var(--text-muted)", fontSize: "12px" }}> (raw system estimate — not yet priced)</span>
                )}
              </span>
            </div>

            {/* Truck pricing — "quoted on request": admin sets the real fare,
                or the scheduler auto-inflates it after 10 minutes if nobody has. */}
            {order.vehicleType === "truck" && (
              <div className="info-row" style={{ background: "rgba(255,255,255,0.03)", padding: "8px 10px", borderRadius: "6px", marginTop: "6px" }}>
                <span className="label">Truck Pricing</span>
                <span className="value">
                  <span className={`status-badge ${TRUCK_PRICING_LABEL[order.truckPricing?.status || "pending"].cls}`} style={{ marginRight: "8px" }}>
                    {TRUCK_PRICING_LABEL[order.truckPricing?.status || "pending"].label}
                  </span>
                  <input
                    type="number"
                    placeholder="Set price (₦)"
                    value={truckPriceMap[order._id] || ""}
                    onChange={e => setTruckPriceMap(m => ({ ...m, [order._id]: e.target.value }))}
                    style={{ width: "120px", display: "inline-block", margin: "4px 8px 0 0", padding: "6px 10px", fontSize: "12px" }}
                  />
                  <button
                    style={{ width: "auto", padding: "6px 14px", fontSize: "12px", marginTop: "4px" }}
                    disabled={truckPricingBusy === order._id}
                    onClick={() => handleSetTruckPrice(order._id)}
                  >
                    {truckPricingBusy === order._id ? "..." : "💰 Set Price"}
                  </button>
                </span>
              </div>
            )}

            {/* Reassignment — only meaningful while the order is still in progress */}
            {!["completed", "cancelled", "failed"].includes(order.status) && (
            <div style={{ marginTop: "12px" }}>
              {riderPickerOrderId === order._id ? (
                <>
                  <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px" }}>Select a dispatcher to assign:</p>
                  {riders.length === 0 ? (
                    <p className="hint">No approved riders available.</p>
                  ) : (
                    riders.map(r => (
                      <div key={r._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                        <div>
                          <strong>{r.name}</strong>
                          <span style={{ color: r.isOnline ? "var(--text-accent)" : "var(--text-muted)", fontSize: "12px", marginLeft: "8px" }}>
                            {r.isOnline ? "🟢 Online" : "⚫ Offline"} · ⭐ {r.rating?.toFixed(1)}
                          </span>
                        </div>
                        <button
                          style={{ width: "auto", padding: "4px 12px", fontSize: "12px", marginTop: 0 }}
                          disabled={reassigning === order._id}
                          onClick={() => handleReassign(order._id, r._id)}>
                          {reassigning === order._id ? "..." : "Assign"}
                        </button>
                      </div>
                    ))
                  )}
                  <button className="secondary" style={{ marginTop: "8px" }} onClick={() => setRiderPickerOrderId(null)}>Cancel</button>
                </>
              ) : (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    style={{ width: "auto", padding: "6px 14px", fontSize: "13px", marginTop: 0 }}
                    className="secondary"
                    onClick={() => setRiderPickerOrderId(order._id)}>
                    🔄 Reassign Rider
                  </button>
                  {(order.paymentMethod === "cash" || order.paymentMethod === "receiver_pay") && (
                    <button
                      style={{ width: "auto", padding: "6px 14px", fontSize: "13px", marginTop: 0 }}
                      className="secondary"
                      disabled={paymentMethodBusy === order._id}
                      onClick={() => handleSwitchPaymentMethod(order._id, order.paymentMethod === "cash" ? "receiver_pay" : "cash")}
                    >
                      {paymentMethodBusy === order._id ? "..." : order.paymentMethod === "cash" ? "💳 Switch to Payment Link" : "💵 Switch to Pay on Delivery"}
                    </button>
                  )}
                  {order.rider && (
                    <button
                      style={{ width: "auto", padding: "6px 14px", fontSize: "13px", marginTop: 0 }}
                      className="secondary"
                      disabled={completeBusy === order._id}
                      onClick={() => handleCompleteOrder(order._id)}
                    >
                      {completeBusy === order._id ? "..." : "✅ Complete Order"}
                    </button>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        ))
      )}
      </div>
    </div>
  );
}
